import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type {
  DemandCell,
  DemandDetail,
  DemandMonth,
  DemandResourceRow,
  DemandRow,
} from "@/api/demands";
import { jsonFetch } from "@/lib/json-fetch";
import { demandKeys } from "@/lib/query";

export type { DemandCell, DemandDetail, DemandMonth, DemandResourceRow, DemandRow };

/** True when a load error came from the NOS Gateway (unconfigured, rejected key, down). */
export function isNosUnavailable(error: Error | null): boolean {
  return error !== null && /Nymbl Platform/i.test(error.message);
}

/** Every NOS Demand the signed-in user may see (Account-scoped server-side). */
export function useDemands() {
  return useQuery<{ demands: DemandRow[] }>({
    queryKey: demandKeys.list(),
    queryFn: () => jsonFetch<{ demands: DemandRow[] }>("/api/dashboard/demands"),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * One Demand's Resources across a six-month window. `windowStart` null = the server's
 * default (the window ending on the current month). The previous window stays on screen
 * while the next one loads, so stepping months does not flash.
 */
export function useDemandDetail(demandId: string, windowStart: string | null, enabled: boolean) {
  const qs = windowStart === null ? "" : `?from=${encodeURIComponent(windowStart)}`;
  return useQuery<DemandDetail>({
    queryKey: demandKeys.detail(demandId, windowStart),
    queryFn: () =>
      jsonFetch<DemandDetail>(`/api/dashboard/demands/${encodeURIComponent(demandId)}${qs}`),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
